import type { Product } from '../types/product'

/**
 * The three Sampler products. Single source of truth for the nav dropdown, the
 * landing ecosystem section, each product page, and the cross-sell band.
 *
 * Signal Orange is intentionally absent from every theme: it belongs to actions
 * across the whole site, never to one product's identity.
 */
export const products: Product[] = [
  {
    slug: 'gone-fishing',
    path: '/products/gone-fishing',
    number: '01',
    name: 'Gone Fishing',
    shortName: 'Gone Fishing',
    summary: 'Location-based product discovery people choose to join.',
    heroEyebrow: 'PRODUCT 01 / THE CATCH EXPERIENCE',
    heroTitle: ['Make discovery', 'feel like play.'],
    heroCopy:
      'Turn attention into participation with location-based product discovery that consumers actively choose to join.',
    outcome: 'Built for the attention → participation moment',
    steps: [
      {
        title: 'CAST',
        copy: 'A campaign opens a product moment in a chosen place, for a chosen audience.',
      },
      {
        title: 'CATCH',
        copy: 'People discover the drop on their phone and claim the product themselves.',
      },
      {
        title: 'REDEEM',
        copy: 'The catch moves through a simple partner redemption flow in the real world.',
      },
      {
        title: 'FEEDBACK',
        copy: 'The experience returns a signal the next campaign decision can use.',
      },
    ],
    centrepiece: 'catch',
    disclosure:
      'PLATFORM DIRECTION — the catch and redemption flow illustrates intended product behaviour, not shipped capability or campaign performance.',
    theme: {
      surface: 'bg-water',
      onSurface: 'text-paper',
      onSurfaceMuted: 'text-paper/70',
      eyebrow: 'text-lagoon',
      hairline: 'border-paper/20',
      dot: 'bg-water',
      dotOnDark: 'bg-lagoon',
      rail: 'bg-water',
      railMark: 'bg-lagoon',
      panel: 'bg-water-light border-lagoon/40',
      onPaper: 'text-water',
    },
  },
  {
    slug: 'surprise',
    path: '/products/surprise',
    number: '02',
    name: 'Surprise Sampling',
    shortName: 'Surprise',
    summary: 'Complementary brands sharing one premium product moment.',
    heroEyebrow: 'PRODUCT 02 / SHARED MOMENTS',
    heroTitle: ['Share the cost.', 'Multiply the impact.'],
    heroCopy:
      'Bring complementary brands together in a premium real-world experience that expands reach without multiplying logistics.',
    outcome: 'Built for the participation → trial moment',
    steps: [
      {
        title: 'PAIR',
        copy: 'Complementary brands are matched into one coherent product moment.',
      },
      {
        title: 'SHARE',
        copy: 'Production, place, and logistics are carried once, not once per brand.',
      },
      {
        title: 'DELIVER',
        copy: 'Consumers receive a curated set that feels intentional, not bundled.',
      },
      {
        title: 'SPLIT',
        copy: 'Each brand reviews its own slice of the shared experience afterwards.',
      },
    ],
    centrepiece: 'pairing',
    disclosure:
      'ILLUSTRATIVE MODEL — pairing structure and cost-sharing framing describe intended operating direction. No commercial terms, pricing, or savings figures are claimed.',
    theme: {
      surface: 'bg-gold',
      onSurface: 'text-ink',
      onSurfaceMuted: 'text-ink/75',
      eyebrow: 'text-ink/65',
      hairline: 'border-ink/25',
      dot: 'bg-gold',
      dotOnDark: 'bg-gold',
      rail: 'bg-gold',
      railMark: 'bg-ink',
      panel: 'bg-paper border-ink/20',
      onPaper: 'text-ink',
    },
  },
  {
    slug: 'sponsor-ai',
    path: '/products/sponsor-ai',
    number: '03',
    name: 'Sponsor AI',
    shortName: 'Sponsor AI',
    summary: 'Match a brand to the right activation context before spending.',
    heroEyebrow: 'PRODUCT 03 / OPPORTUNITY MATCHING',
    heroTitle: ['Find the right', 'stage for', 'your brand.'],
    heroCopy:
      'Match your brand to relevant audiences, event opportunities, and activation contexts before budget is committed.',
    outcome: 'Built for the target → activate moment',
    steps: [
      {
        title: 'DEFINE',
        copy: 'State the audience, category, and campaign ambition you are working toward.',
      },
      {
        title: 'MATCH',
        copy: 'Candidate audiences, events, and activation contexts are ranked against that brief.',
      },
      {
        title: 'REVIEW',
        copy: 'Compare the shortlist on fit and reasoning before any budget is committed.',
      },
      {
        title: 'ACTIVATE',
        copy: 'Take the chosen context straight into a Sampler campaign.',
      },
    ],
    centrepiece: 'match',
    disclosure:
      'PLATFORM CONCEPT — matching behaviour and fit scores shown here are illustrative interface direction, not model output or validated accuracy.',
    theme: {
      surface: 'bg-iris',
      onSurface: 'text-paper',
      onSurfaceMuted: 'text-paper/70',
      eyebrow: 'text-paper/70',
      hairline: 'border-paper/20',
      dot: 'bg-iris',
      dotOnDark: 'bg-iris-light',
      rail: 'bg-iris',
      railMark: 'bg-paper',
      panel: 'bg-iris-light border-paper/25',
      onPaper: 'text-iris',
    },
  },
]

export function getProduct(slug: string): Product | undefined {
  return products.find((product) => product.slug === slug)
}

/** The other two products, for a product page's cross-sell band. */
export function getSiblings(slug: string): Product[] {
  return products.filter((product) => product.slug !== slug)
}
